import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { clearSessionStorage, getNewToken, postRequest } from '../../services/httpservice';
import { apiURL } from '../../constants/apiUrlConstants';
import axios, { AxiosError } from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../components/Loader';
import KuwaitiDinarFormat from '../../components/KuwaitiDinarFormat';
import xpay from '../../assets/xpay.png';
import i18n from "../../i18n/i18n";

interface DealDetail {
    type: string;
    imgUrl: string;
    loanAccountNo: string;
    invoiceNumber: string;
    pendingInstallments: string;
    installmentDue: string;
    outstandingAmount: string;
    lateInstallment: string;
    lateInstallmentAmount: string;
    cardNo: string;
}

interface ChangeCardModalProps {
    open: boolean;
    onClose: () => void;
    loanAccountNo: string;
    installmentDue: string;
}
const getLocalizedMessage = (message: string) => {
    const parts = message.split("/");
    if (i18n.language === "ar" && parts.length > 1) {
        return parts[1].trim();
    }
    return parts[0].trim();
};
const ChangeCardModal = ({ open, onClose, loanAccountNo, installmentDue }: ChangeCardModalProps) => {
    const [selected, setSelected] = useState<"kd" | "installment">("installment");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleContinue = async () => {
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            if (!accessToken) {
                clearSessionStorage();
                navigate('/');
                return;
            }
            const civilId = sessionStorage.getItem('civilId');
            const amount = selected === 'kd' ? 1 : Number(installmentDue);
            setIsLoading(true);
            const response = await postRequest(
                process.env.REACT_APP_API_URL + apiURL.GET_SESSION_DETAILS,
                {
                    civilId,
                    loanAccountNo,
                    paymentType: 'CARDCHANGE',
                    amount,
                }
            );
            setIsLoading(false);
            if (response == null) {
                toast.dismiss();
                toast.error(process.env.REACT_APP_ERROR_MSG);
                return;
            }
            if (response.status === 401) {
                toast.error('Session expired');
                clearSessionStorage();
                setTimeout(() => { navigate('/'); }, 2000);
                return;
            }
            if (response.data === '') {
                toast.dismiss();
                toast.error(process.env.REACT_APP_ERROR_MSG);
                return;
            }
            const data = response.data;
            if (data != null) {
                if (data.status === 'S' && data.sessionResponse) {
                    sessionStorage.setItem('checkoutData', JSON.stringify(data));
                    sessionStorage.setItem('checkoutLoanAccountNo', loanAccountNo);
                    getNewToken();
                    navigate('/home/checkout');
                } else if (data.status === 'E') {
                    toast.error(data.message ? getLocalizedMessage(data.message) : process.env.REACT_APP_ERROR_MSG);
                    onClose();
                } else {
                    toast.error(data.message || process.env.REACT_APP_ERROR_MSG);
                }
            }
        } catch (error) {
            setIsLoading(false);
            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError;
                if (axiosError.response) {
                    if (axiosError?.response.status === 401) {
                        toast.error('Session expired');
                        clearSessionStorage();
                        setTimeout(() => { navigate('/'); }, 2000);
                    } else {
                        toast.error(process.env.REACT_APP_ERROR_MSG);
                    }
                }
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: "563px" },
                    maxWidth: "563px",
                    borderRadius: "10px",
                    p: { xs: "32px 20px", sm: "52px 32px" },
                    overflow: "hidden",
                },
            }}
        >
            {isLoading && <Loader />}
            <DialogContent  sx={{
     padding: "0 !important",             
    flex: "unset",     
    overflowY: "unset"  
  }}>
                {/* close button */}
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        color: "#333",
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                {/* title */}
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: "17px",
                        color: "#111",
                        mb: 3,
                        pr: 3,
                        lineHeight: 1.4,
                    }}
                >
                    {t('dealshistory.tochangeyourcard')}
                </Typography>

                {/* option 1 – Pay 1 KD */}
                <Box
                    onClick={() => setSelected("kd")}
                    sx={{
                        border: selected === "kd" ? "2px solid #2493C7" : "1.5px solid #E0E0E0 ",
                        borderRadius: "4px",
                        p: "27px 27px",
                        mb: "16px",
                        cursor: "pointer",
                        background: selected === "kd" ? "#EAF5FB" : "#fff",
                        transition: "all 0.18s",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: selected === "kd" ? 600 : 400,
                            color: selected === "kd" ? "#2493C7" : "#111",
                            fontSize: "15px",
                        }}
                    >
                       {t('dealshistory.pay1kd')}
                    </Typography>
                </Box>

                {/* option 2 – Pay 1 Monthly Installment */}
                <Box
                    onClick={() => setSelected("installment")}
                    sx={{
                        border: selected === "installment" ? "2px solid #2493C7" : "1.5px solid #E0E0E0",
                        borderRadius: "4px",
                        p: "27px",
                        mb: "32px",
                        cursor: "pointer",
                        background: selected === "installment" ? "#EAF5FB" : "#fff",
                        transition: "all 0.18s",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: selected === "installment" ? 700 : 400,
                            color: selected === "installment" ? "#2493C7" : "#111",
                            fontSize: "14px",
                        }}
                    >
                       {t('dealshistory.pay1monthinstallment')}
                    </Typography>
                </Box>

                {/* continue button */}
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleContinue}
                    disabled={isLoading}
                     sx={{
                        background: "#2493C7",
                        fontWeight: 500,
                        fontSize: "14px",
                        padding: "6px 16px",
                        borderRadius: "4px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        boxShadow: "none",
                        "&:hover": { background: "#1a7aaa", boxShadow: "none" },
                    }}
                >
                    {t('dealshistory.continue')}
                </Button>
            </DialogContent>
        </Dialog>
    );
};
// const DealsHistory = () => {
//       const { t } = useTranslation();
//     const [modalOpen, setModalOpen] = useState<boolean>(false);
//      const navigate = useNavigate();
//      const CHANGE_CARD_WIDTH = 120;
// const CARD_WIDTH = 180;

//     return (
//         <Box sx={{ width: "100%" }}>
//             <TableContainer
//               sx={{
//      overflowX: "auto",
//     width: "100%",
//     "&::-webkit-scrollbar": {
//       height: "8px",
//     },
//   }}
//   >
//              <Table
//     sx={{
//           borderCollapse: "separate",
//     borderSpacing: 0,
//     minWidth: 1960,// adjust as needed
  
//     }}
// >

//     <TableHead>
//         <TableRow >

//             <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                         padding: "15px 10px",
//                         height: "106px",
//                     fontWeight: 700
//                 }}
//             >
//                 Type
//             </TableCell>

//             <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                     fontWeight: 700
//                 }}
//             >
//                 Account
//             </TableCell>

//             <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                     fontWeight: 700
//                 }}
//             >
//                 Invoice
//             </TableCell>

//             <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                     fontWeight: 700
//                 }}
//             >
//                 Pending Payments
//             </TableCell>

//             <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                     fontWeight: 700
//                 }}
//             >
//                 Payment Amount 
//             </TableCell>
//  <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                     fontWeight: 700
//                 }}
//             >
//               Outstanding Amount
//             </TableCell> 
//             <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                     fontWeight: 700
//                 }}
//             >
//                 Late Installement 
//             </TableCell>
//              <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                     fontWeight: 700
//                 }}
//             >
//                 Late Installement Amount
//             </TableCell>
//              <TableCell
//                 align="center"
//                 sx={{
//                     border: "1px solid #E0E0E0 ",
//                     fontWeight: 700,
                       
//                 }}
//             >
//                 Pay
//             </TableCell>
//     {/* <TableCell
//   align="center"
//   sx={{
//     border: "1px solid #E0E0E0 ",
//     fontWeight: 700,
//     position: "sticky",
//     right: CHANGE_CARD_WIDTH,
//     backgroundColor: "#fff",
//     zIndex: 20,
// //  minWidth: `${CHANGE_CARD_WIDTH}px`,
// //     maxWidth: `${CHANGE_CARD_WIDTH}px`,
//     borderLeft: "2px solid #E0E0E0 ",
//     boxShadow: "-6px 0 8px rgba(0,0,0,0.08)",
//   }}
// >
//   Card ending with
// </TableCell>

// <TableCell
//   align="center"
//   sx={{
//     border: "1px solid #E0E0E0 ",
//     fontWeight: 700,
//     position: "sticky",
//     right: 0,
//     backgroundColor: "#fff",
//     zIndex: 21,
//     // minWidth: `${CHANGE_CARD_WIDTH}px`,
//     // maxWidth: `${CHANGE_CARD_WIDTH}px`,
//     borderLeft: "2px solid #E0E0E0 ",
//   }}
// >
//   Change Card
// </TableCell> */}
// {/* Card ending with — HEADER */}
// <TableCell
//   align="center"
//   sx={{
//     border: "1px solid #E0E0E0 ",
//     fontWeight: 700,
//     position: "sticky",
//     right: 140,
  
//     backgroundColor: "#fff",
//     zIndex: 20,
// //     minWidth: `${CARD_WIDTH}px`,   // ← match body
// //     maxWidth: `${CARD_WIDTH}px`,   // ← match body
// //     borderLeft: "2px solid #E0E0E0 ",
// //      transform: "translateZ(0)",      // ← promote to own layer
// //   willChange: "transform",         // ← hint the browser
//   backfaceVisibility: "hidden",
//     boxShadow: "-10px 0 8px rgba(9, 9, 9, 0.08)",
//   }}
// >
//   {t('dealshistory.cardendingwith')}

// </TableCell>

// {/* Change Card — HEADER */}
// <TableCell
//   align="center"
//   sx={{
//     border: "1px solid #E0E0E0 ",
//     fontWeight: 700,
//     position: "sticky",
//     right: 0,
//     backgroundColor: "#fff",
//     zIndex: 21,
//     minWidth: `${CHANGE_CARD_WIDTH}px`,  // ← match body
//     maxWidth: `${CHANGE_CARD_WIDTH}px`,  // ← match body
//     // borderLeft: "2px solid #E0E0E0 ",
//   }}
// >
//    {t('dealshistory.changecard')}
// </TableCell>
//         </TableRow>
//     </TableHead>

//     <TableBody>
//         {dealsData.map((item, index) => (
//             <TableRow key={index}>

//                 <TableCell
//                     align="center"
//                     sx={{ border: "1px solid #E0E0E0 " ,    padding: "10px 16px"}}
//                 >
//                       <img src={xpay} alt="Logo" style={{ width: '88px' }} />
                    
//                 </TableCell>

//                 <TableCell
//                     align="center"
//                     sx={{ border: "1px solid #E0E0E0 " ,    padding: "10px 16px"}}
//                 >
//                     {item.account}
//                 </TableCell>

//                 <TableCell
//                     align="center"
//                     sx={{ border: "1px solid #E0E0E0 ",    padding: "10px 16px" }}
//                 >
//                     <Typography color="#001B5E">
//                         {item.invoice}
//                     </Typography>
//                 </TableCell>

//                 <TableCell
//                     align="center"
//                     sx={{ border: "1px solid #E0E0E0 ",    padding: "10px 16px"}}
//                 >
//                     {item.pending}
//                 </TableCell>

//                 <TableCell
//                     align="center"
//                     sx={{ border: "1px solid #E0E0E0 " ,    padding: "10px 16px"}}
//                 >
//                     <Typography color="#001B5E">
//                         {item.value}
//                     </Typography>
//                 </TableCell>
//      <TableCell
//                     align="center"
//                     sx={{ border: "1px solid #E0E0E0 " ,    padding: "10px 16px"}}
//                 >
//                     <Typography color="#001B5E">
//                         {item.outstanding}
//                     </Typography>
//                 </TableCell>     <TableCell
//                     align="center"
//                     sx={{ border: "1px solid #E0E0E0 " ,    padding: "10px 16px"}}
//                 >
//                     <Typography color="#001B5E">
//                         {item.late}
//                     </Typography>
//                 </TableCell>     <TableCell
//                     align="center"
//                     sx={{ border: "1px solid #E0E0E0 " ,    padding: "10px 16px"}}
//                 >
//                     <Typography color="#001B5E">
//                         {item.lateInstallmentamount}
//                     </Typography>
//                 </TableCell>
//                     <TableCell
//                       className="freeze"
//                       sx={{
//                       border: "1px solid #E0E0E0 " ,    padding: "10px 16px",
                        
//                         backgroundColor: { xs: "#F8F8F8", sm: "#FFF" },
//                         width: { xs: "21px", md: "auto" },
//                       }}
//                     >
//          <Button 
//            variant="contained"
//            size="medium"
//            className="deals-linkStyle"
//             onClick={() => navigate("/home/installments")}
//              sx={{
//        backgroundColor: "#2A92BD",
//        "&:hover": {
//          backgroundColor: "#2A92BD",
//        },
//      }}
//          >
//            Pay
//          </Button>
//        </TableCell>
// {/* <TableCell
//   align="center"
//   sx={{
//     border: "1px solid #E0E0E0 ",
//     position: "sticky",
//     right: CHANGE_CARD_WIDTH,
//     backgroundColor: "#fff",
//     zIndex: 10,
//     minWidth: `${CARD_WIDTH}px`,
//     maxWidth: `${CARD_WIDTH}px`,
//     borderLeft: "2px solid #E0E0E0 ",
//     boxShadow: "-4px 0 8px rgba(0,0,0,0.08)",
//   }}
// >
//   <Box
//     sx={{
//         border: "1px solid #E0E0E0 ",
//     position: "sticky",
//     right: 0,
//       p: 1,
//       borderRadius: "4px",
//     }}
//   >
//     {item.card}
//   </Box>
// </TableCell> */}
// <TableCell
//   align="center"
//   sx={{
//     border: "1px solid #E0E0E0 ",
//     position: "sticky",
//     right: 140,
//     backgroundColor: "#fff",
//     zIndex: 10,
//     minWidth: `${CARD_WIDTH}px`,
//     maxWidth: `${CARD_WIDTH}px`,
//     borderLeft: "2px solid #E0E0E0 ",
//     boxShadow: "-10px 0 8px rgba(0,0,0,0.08)",
//   }}
// >
//   <Box sx={{ border: "1px solid #E0E0E0 ", p: 1, borderRadius: "4px" ,background:"#E0E0E0"}}>
//     {item.card}
//   </Box>
// </TableCell>
// <TableCell
//   align="center"
//   sx={{
//     border: "1px solid #E0E0E0 ",
//     position: "sticky",
//     right: 0,
//     backgroundColor: "#fff",
//     zIndex: 11,
//     minWidth: `${CHANGE_CARD_WIDTH}px`,
//     maxWidth: `${CHANGE_CARD_WIDTH}px`,
//     borderLeft: "2px solid #E0E0E0 ",
//   }}
// >
//   <Button
//     variant="contained"
//     onClick={() => setModalOpen(true)}
//     sx={{
//       background: "#2493C7",
//       fontWeight: 700,
//       "&:hover": {
//         background: "#2493C7",
//       },
//     }}
//   >
//      {t('dealshistory.change')}
//   </Button>
// </TableCell>

//             </TableRow>
//         ))}
//     </TableBody>

// </Table>
//             </TableContainer>
//             <ChangeCardModal open={modalOpen} onClose={() => setModalOpen(false)} />
//         </Box>
//     );
// };
const DealsHistory = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedDeal, setSelectedDeal] = useState<{ loanAccountNo: string; installmentDue: string } | null>(null);
  const [deals, setDeals] = useState<DealDetail[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [civilId, setCivilId] = useState<string | null>(null);
const isRTL = i18n.language === "ar";

  const navigate = useNavigate();

  useEffect(() => {
    const storedCivilId = sessionStorage.getItem('civilId');
    if (storedCivilId) {
      setCivilId(storedCivilId);
    }
  }, []);

  useEffect(() => {
    if (civilId) {
      getDealDetails();
    }
  }, [civilId]);

  const getDealDetails = async () => {
    setIsLoading(true);
    try {
      const accessToken = sessionStorage.getItem('accessToken');
      if (!accessToken) {
        clearSessionStorage();
        navigate('/');
        return;
      }
      const response = await postRequest(
        process.env.REACT_APP_API_URL + apiURL.GET_DEALS,
        { civilId: civilId }
      );
      setIsLoading(false);
      if (response == null) {
        toast.dismiss();
        toast.error(process.env.REACT_APP_ERROR_MSG);
        return;
      }
      if (response.status === 401) {
        toast.dismiss();
        toast.error('Session expired');
        clearSessionStorage();
        setTimeout(() => { navigate('/'); }, 2000);
        return;
      }
      if (response.data === '') {
        toast.dismiss();
        toast.error(process.env.REACT_APP_ERROR_MSG);
        return;
      }
      const data = response.data;
      setDeals(data.dealDetails || []);
      getNewToken();
    } catch (error) {
      setIsLoading(false);
      if (axios.isAxiosError(error)) {
        toast.dismiss();
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          if (axiosError?.response.status === 401) {
            toast.error('Session expired');
            clearSessionStorage();
            setTimeout(() => { navigate('/'); }, 2000);
          } else {
            toast.error(process.env.REACT_APP_ERROR_MSG);
          }
        }
      }
    }
  };

  function getImgsrc(imgPath: string) {
    try {
      return require('../../' + imgPath);
    } catch (error) {
      return xpay;
    }
  }
 
  // Frozen column widths (rightmost first)

  const CHANGE_CARD_WIDTH = 200; // last column ("Change Card")

  const CARD_WIDTH = 200;        // second-to-last ("Card ending with")
 
  // Shared sticky styles so HEADER and BODY always line up
// commented 24-6-2026 start
//   const stickyChangeCard = {
//   position: "sticky",
//   right: 0,
//   width: { xs: 70, sm: 120, md: 200 },
//   minWidth: { xs: 70, sm: 120, md: 200 },
//   maxWidth: { xs: 70, sm: 120, md: 200 },
//   backgroundColor: "#fff",
//   borderLeft: "2px solid #E0E0E0",
//   zIndex: 11,
// };

// const stickyCardEnding = {
//   position: "sticky",
//   right: { xs: 70, sm: 120, md: 200 },
//   width: { xs: 90, sm: 150, md: 200 },
//   minWidth: { xs: 90, sm: 150, md: 200 },
//   maxWidth: { xs: 90, sm: 150, md: 200 },
//   backgroundColor: "#fff",
//   borderLeft: "2px solid #E0E0E0",
//   boxShadow: "-8px 0 8px rgba(0,0,0,0.08)",
//   zIndex: 10,
// };
// commented 24-6-2026 end
const stickyChangeCard = {
  position: "sticky" as const,

  ...(isRTL
    ? { left: 0 }
    : { right: 0 }),

  width: { xs: 110, sm: 120, md: 200 },
  minWidth: { xs: 110, sm: 120, md: 200 },
  maxWidth: { xs: 110, sm: 120, md: 200 },

  backgroundColor: "#fff",
  borderLeft: "2px solid #E0E0E0",
  zIndex: 11,
};

const stickyCardEnding = {
  position: "sticky" as const,

  ...(isRTL
    ? { left: { xs: 110, sm: 120, md: 200 } }
    : { right: { xs: 110, sm: 120, md: 200 } }),

  width: { xs: 120, sm: 150, md: 200 },
  minWidth: { xs: 120, sm: 150, md: 200 },
  maxWidth: { xs: 120, sm: 150, md: 200 },

  backgroundColor: "#fff",
  borderLeft: "2px solid #E0E0E0",
  boxShadow: isRTL
    ? "8px 0 8px rgba(0,0,0,0.08)"
    : "-8px 0 8px rgba(0,0,0,0.08)",
  zIndex: 10,
};
//   const stickyChangeCard = {

//     position: "sticky" as const,

//     right: 0,

//     minWidth: { xs: 90, sm: CHANGE_CARD_WIDTH },

//     maxWidth: { xs: 90, sm: CHANGE_CARD_WIDTH },

//     backgroundColor: "#fff",

//     borderLeft: "2px solid #E0E0E0",

//     zIndex: 11,

//   };
 
//   const stickyCardEnding = {

//     position: "sticky" as const,

//     // right offset MUST equal the width of the column to its right

//     right: { xs: 90, sm: CHANGE_CARD_WIDTH },

//     minWidth: { xs: 150, sm: CARD_WIDTH },

//     maxWidth: { xs: 150, sm: CARD_WIDTH },

//     backgroundColor: "#fff",

//     borderLeft: "2px solid #E0E0E0",

//     boxShadow: "-8px 0 8px rgba(0,0,0,0.08)",

//     backfaceVisibility: "hidden" as const,

//     zIndex: 10,

//   };
//  const stickyCardEnding = {
//   position: "sticky",
//   right: { xs: 90, sm: 200 },
//   width: { xs: 150, sm: 200 },
//   minWidth: { xs: 150, sm: 200 },
//   maxWidth: { xs: 150, sm: 200 },
//   backgroundColor: "#fff",
//   borderLeft: "2px solid #E0E0E0",
//   boxShadow: "-8px 0 8px rgba(0,0,0,0.08)",
//   zIndex: 10,
// };
  // Common cell sizing, responsive

  const cellBase = {

    border: "1px solid #E0E0E0",

    padding: { xs: "8px 8px", sm: "10px 16px" },

    fontSize: { xs: "0.8rem", sm: "0.875rem" },

    whiteSpace: "nowrap" as const,

  };
 
  return (

<Box sx={{ width: "100%" }}>
      {isLoading && <Loader />}

<Box sx={{ borderTop: "1px solid #E0E0E0", mx: { xs: -3, md: -6 } }} />

<TableContainer

        sx={{

         overflowX: deals.length === 0 ? "hidden" : "auto",

          width: "100%",

          WebkitOverflowScrolling: "touch", // smooth momentum scroll on iOS

          "&::-webkit-scrollbar": { height: "8px" },

          "&::-webkit-scrollbar-thumb": {

            backgroundColor: "#C4C4C4",

            borderRadius: "4px",

          },

        }}
>
<Table

          sx={{

            borderCollapse: "separate",

            borderSpacing: 0,

            minWidth: 1900, // forces horizontal scroll; tune to your columns

          }}
>
<TableHead >
<TableRow className="tableRow"
                sx={{
                  "& .MuiTableCell-root": {
                  height: "106px",
                  color:"rgba(0, 16, 75, 0.87)",
                  verticalAlign: "middle",
                  borderTop: "none",
                  },
                }}>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}>  {t("dealshistory.type")}</TableCell>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}>  {t("dealshistory.account")}</TableCell>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}> {t("dealshistory.invoice")}</TableCell>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}>  {t("dealshistory.pendingpayments")}</TableCell>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}> {t("dealshistory.paymentamount")}</TableCell>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}> {t("dealshistory.outstandingamount")}</TableCell>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}>{t("dealshistory.lateinstallement")}</TableCell>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}>{t("dealshistory.lateinstallementamount")}</TableCell>
<TableCell align="center" sx={{ ...cellBase, fontWeight: 700 }}> {t("dealshistory.pay")}</TableCell>
 
              {/* Frozen columns — use the SAME sticky objects as the body */}
<TableCell align="center"   sx={{
    ...cellBase,
    ...stickyCardEnding,
    fontWeight: 700,
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.1,
    fontSize: { xs: "0.65rem", sm: "0.875rem" },
    p: { xs: 0.5, sm: 2 },
  }}>

              {t("dealshistory.cardendingwith")}
</TableCell>
<TableCell align="center" sx={{ ...cellBase, ...stickyChangeCard, fontWeight: 700,   whiteSpace: "normal",wordBreak: "break-word",
    lineHeight: 1.1,
    fontSize: { xs: "0.65rem", sm: "0.875rem" },   p: { xs: 0.5, sm: 2 },}}>

           {t("dealshistory.changecard")}
</TableCell>
</TableRow>
</TableHead>
 
          <TableBody sx={{
    "& .MuiTableRow-root": {
      height: "54px",
    },
    "& .MuiTableCell-root": {
      height: "54px",
      padding: "0 16px",
      verticalAlign: "middle",
      boxSizing: "border-box",
      fontSize: "14px !important",
    },
  }}>

            {deals.map((item, index) => (
<TableRow key={index}>
<TableCell align="center" sx={cellBase}>
<img src={getImgsrc(item.imgUrl)} alt="Logo" style={{ width: "88px" }} />
</TableCell>
<TableCell align="center" sx={cellBase}>{item.loanAccountNo}</TableCell>
<TableCell align="center" sx={cellBase}>
<Typography sx={{ color: "#00104B" ,fontSize:"14px"}}>{item.invoiceNumber}</Typography>
</TableCell>
<TableCell align="center" sx={cellBase}>{item.pendingInstallments}</TableCell>
<TableCell align="center" color="#00104B" sx={cellBase}>
    <Typography sx={{ color: "#00104B" ,fontSize:"14px"}}>
<KuwaitiDinarFormat  amount={Number(item.installmentDue)} /></Typography>
</TableCell>
<TableCell align="center" sx={cellBase}>
      <Typography sx={{ color: "#00104B" ,fontSize:"14px"}}>
<KuwaitiDinarFormat amount={Number(item.outstandingAmount)} /></Typography>
</TableCell>
<TableCell align="center" sx={cellBase}>
<Typography>{item.lateInstallment}</Typography>
</TableCell>
<TableCell align="center" sx={cellBase}>
    <Typography sx={{ color: "#00104B" ,fontSize:"14px"}}>
<KuwaitiDinarFormat amount={Number(item.lateInstallmentAmount)} /></Typography>
</TableCell>
<TableCell align="center" sx={cellBase}>
<Button
                    variant="contained"
                    size="medium"
                    className="deals-linkStyle"
                    onClick={() => navigate("/home/installments", { state: { loanAccountNo: item.loanAccountNo, type: item.type, from: "profile", activeTab: "deals" } })}
                    sx={{ backgroundColor: "#2A92BD", "&:hover": { backgroundColor: "#2A92BD" } }}
>
                     {t("dealshistory.pay")}
</Button>
</TableCell>

                {/* Frozen body cells — SAME sticky objects as header */}
<TableCell align="center" sx={{ ...cellBase, ...stickyCardEnding }}>
<Box sx={{
    width: "100%",
    maxWidth: { xs: 80, sm: "100%" },
    boxSizing: "border-box",
    border: "1px solid #E0E0E0",
    p: { xs: 0.5, sm: 1 },
    borderRadius: "4px",
    backgroundColor: "#E0E0E0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: { xs: "0.7rem", sm: "0.875rem" },
  }}>
                    {item.cardNo}
</Box>
</TableCell>
<TableCell align="center" sx={{ ...cellBase, ...stickyChangeCard }}>
<Button
  variant="contained"
  onClick={() => { setSelectedDeal({ loanAccountNo: item.loanAccountNo, installmentDue: item.installmentDue }); setModalOpen(true); }}
  sx={{
    background: "#2493C7",
    fontWeight: 700,
    fontSize: { xs: "12px", sm: "14px" },
    padding: { xs: "6px 10px", sm: "6px 16px" },
    minWidth: "unset",
    "&:hover": { background: "#2493C7" },
  }}
>
  {t("dealshistory.change")}
</Button>
</TableCell>
</TableRow>

            ))}
</TableBody>
</Table>
</TableContainer>

      {deals.length === 0 && !isLoading && (
        <Box sx={{
          textAlign: 'center',
          py: 4,
          color: '#888',
          fontSize: '14px',
          border: '1px solid #E0E0E0',
          borderTop: 'none',
          background: '#fff',
        }}>
          {t('dealshistory.noDataFound', 'No data found')}
        </Box>
      )}

      <ChangeCardModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedDeal(null); }}
        loanAccountNo={selectedDeal?.loanAccountNo ?? ''}
        installmentDue={selectedDeal?.installmentDue ?? '0'}
      />
      <ToastContainer position="top-right" newestOnTop limit={1} />
</Box>

  );

};
 
export default DealsHistory;